import {
  RECURRING_PAID_PLANS,
  hasActivePaidAccess,
  normalizeSubscriptionType,
} from './subscriptionAccess';

const ACTIVE_SUBSCRIPTION_STATUSES = new Set(['active', 'trialing']);

const normalizeValue = (value) => (
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/-/g, '_')
);

export const normalizeSubscriptionStatus = (subscription) => (
  normalizeValue(subscription?.status)
);

export const normalizeSubscriptionPlanType = (subscription) => (
  normalizeValue(subscription?.plan_type)
);

export const hasActivePaidSubscription = (subscription) => {
  const status = normalizeSubscriptionStatus(subscription);
  const planType = normalizeSubscriptionPlanType(subscription);

  if (!ACTIVE_SUBSCRIPTION_STATUSES.has(status)) return false;
  return RECURRING_PAID_PLANS.has(planType) || planType === 'exam_pass';
};

const buildMismatch = ({
  profile,
  profileFresh,
  profilePaid,
  subscription,
  subscriptionFresh,
  subscriptionPaid,
}) => {
  if (!profileFresh || !subscriptionFresh || !subscription) return null;

  const profilePlan = normalizeSubscriptionType(profile);
  const subscriptionPlan = normalizeSubscriptionPlanType(subscription);
  const subscriptionStatus = normalizeSubscriptionStatus(subscription);

  if (!subscriptionStatus) return null;

  if (subscriptionPaid && !profilePaid) {
    return {
      type: 'subscription_paid_profile_unpaid',
      profilePlan,
      subscriptionPlan,
      subscriptionStatus,
    };
  }

  if (profilePaid && !subscriptionPaid && subscriptionPlan) {
    return {
      type: 'profile_paid_subscription_unpaid',
      profilePlan,
      subscriptionPlan,
      subscriptionStatus,
    };
  }

  if (
    profilePaid &&
    subscriptionPaid &&
    profilePlan &&
    subscriptionPlan &&
    profilePlan !== subscriptionPlan
  ) {
    return {
      type: 'paid_plan_mismatch',
      profilePlan,
      subscriptionPlan,
      subscriptionStatus,
    };
  }

  return null;
};

export const resolveAccessState = ({
  user,
  profile,
  subscription,
  loading = false,
  profileFresh = false,
  subscriptionFresh = false,
  now = new Date(),
} = {}) => {
  const authenticated = Boolean(user?.id || user);

  if (!authenticated) {
    return {
      status: loading ? 'loading' : 'signed_out',
      hasPaidAccess: false,
      needsOnboarding: false,
      redirectTarget: null,
      reason: loading ? 'auth_loading' : 'signed_out',
      profileFresh,
      subscriptionFresh,
      mismatch: null,
    };
  }

  const profilePaid = profileFresh && hasActivePaidAccess(profile, now);
  const subscriptionPaid = subscriptionFresh && hasActivePaidSubscription(subscription);
  const mismatch = buildMismatch({
    profile,
    profileFresh,
    profilePaid,
    subscription,
    subscriptionFresh,
    subscriptionPaid,
  });

  if (profilePaid || subscriptionPaid) {
    return {
      status: 'paid',
      hasPaidAccess: true,
      needsOnboarding: false,
      redirectTarget: null,
      reason: mismatch?.type === 'subscription_paid_profile_unpaid'
        ? 'subscription_reconciles_profile'
        : profilePaid
          ? 'profile_paid'
          : 'subscription_paid',
      profileFresh,
      subscriptionFresh,
      mismatch,
    };
  }

  if (!profileFresh || !subscriptionFresh) {
    return {
      status: 'verifying',
      hasPaidAccess: false,
      needsOnboarding: false,
      redirectTarget: null,
      reason: 'awaiting_current_user_verification',
      profileFresh,
      subscriptionFresh,
      mismatch,
    };
  }

  if (!profile) {
    return {
      status: 'needs_profile',
      hasPaidAccess: false,
      needsOnboarding: false,
      redirectTarget: null,
      reason: 'profile_unavailable',
      profileFresh,
      subscriptionFresh,
      mismatch,
    };
  }

  if (!profile.onboarding_completed) {
    return {
      status: 'needs_onboarding',
      hasPaidAccess: false,
      needsOnboarding: true,
      redirectTarget: '/onboarding',
      reason: 'free_requires_onboarding',
      profileFresh,
      subscriptionFresh,
      mismatch,
    };
  }

  return {
    status: 'unpaid',
    hasPaidAccess: false,
    needsOnboarding: false,
    redirectTarget: '/membership',
    reason: 'unpaid',
    profileFresh,
    subscriptionFresh,
    mismatch,
  };
};
